import type { SessionAnswer } from '@beyond-js/ui/dom';
import { Refusal } from '@accounts/transport';
import type { Accounts } from './identity';
import { Resume } from './resume';

/**
 * What this interface gives the family's session rule (`Session` of `@beyond-js/ui`, D63): its own
 * session read and the ways to sign in again.
 *
 * Accounts is the authority, so its case differs from every product's. There is no silent renewal:
 * the Beyond session is proved only by signing in here, so `start('silent')` is null and an ended
 * session asks the person at once. **Continue as {name}** opens this interface's own sign-in in a
 * window (`/signin?mode=window`), which ends on the landing (`Resume.landed`); the platform cookie
 * that window sets is the one this page sends, so its read then says signed. A browser that blocks
 * the window signs in in this tab (`/signin?to=<here>`), which comes back here.
 */
export class Renewal {
	/** The product id the session and its landing carry */
	static PRODUCT = 'accounts';

	#accounts: Accounts;
	#location: Pick<Location, 'origin' | 'pathname' | 'search'>;
	#hint?: string;

	/**
	 * @param location Where this page is, read when an address is asked for
	 * @param hint The signed-in person's address: the sign-in that renews it opens with it filled in, so
	 * "Continue as {name}" asks only for what proves it is them (ACC-17)
	 */
	constructor(accounts: Accounts, location: Pick<Location, 'origin' | 'pathname' | 'search'> = globalThis.location, hint?: string) {
		this.#accounts = accounts;
		this.#location = location;
		this.#hint = hint;
	}

	#hinted(): string {
		return this.#hint ? `&hint=${encodeURIComponent(this.#hint)}` : '';
	}

	/** "Open Beyond Accounts" for a suspended account: this interface's own account page */
	get account(): string {
		return `${this.#location.origin}/account`;
	}

	/**
	 * The session's own read: `signed` with the person, `ended` with Accounts' reason, and a rejection
	 * for anything that is not a verdict about the session, which `Session` treats as unavailable
	 */
	async read(): Promise<SessionAnswer> {
		try {
			// Read for itself: an ended session is this read's answer, not something to renew
			const identity = await this.#accounts.identity({ ended: 'caller' });
			return { state: 'signed', person: { id: identity.account.id, name: identity.account.name ?? null }, expires: identity.session.expires };
		} catch (error) {
			if (Refusal.is(error) && error.lapsed) return { state: 'ended', reason: error.reason };
			throw error;
		}
	}

	/** Where signing in again starts: never silently; in a window that ends on the landing; or in this tab */
	start(mode: 'silent' | 'window' | 'tab'): string | null {
		const origin = this.#location.origin;
		if (mode === 'window') return `${origin}/signin?mode=${Resume.WINDOW}${this.#hinted()}`;
		if (mode === 'tab') return `${origin}/signin?to=${encodeURIComponent(`${this.#location.pathname}${this.#location.search}`)}${this.#hinted()}`;
		return null;
	}
}
